import React, { useEffect, useMemo, useState } from 'react';
import { MdCheck } from 'react-icons/md';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import axios from 'config/axios';
import Header from 'components/Header';
import Loader from 'components/Loader';
import FillAppTabs from 'components/FillAppTabs';
import { useFillAppColleges } from 'hooks/useFillAppColleges';
import {
    type ChecklistItem,
    type College,
    type CollegeChoice,
    TEST_POLICY_LABELS,
    buildChecklistItems,
    combineRequirements,
    deadlineSummary,
    feeLabel,
    formatDeadline,
    recsSummary,
} from 'global/colleges';
import { DEFAULT_CYCLE_YEAR } from 'global/fillapp';

import './index.css';

type ReviewPayload = {
    studentName: string;
    fillApp: { cycleYear: number; colleges: CollegeChoice[]; collegeChecklist: Record<string, boolean> } | null;
};

// Right panel: one date-grouped checklist mirroring the student view,
// read-only — items appear under their earliest effective due date.
function CombinedPanel({ colleges, plans, checklist }: {
    colleges: College[];
    plans: Record<string, string | null | undefined>;
    checklist: Record<string, boolean>;
}) {
    const req = combineRequirements(colleges, plans);
    const items = buildChecklistItems(req);

    const groups = new Map<string | null, ChecklistItem[]>();
    for (const item of items) {
        const key = item.dueBy || null;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key)!.push(item);
    }
    const dates = [...groups.keys()].filter((d): d is string => !!d).sort();

    const row = (item: ChecklistItem) => {
        const checked = !!checklist[item.key];
        return (
            <div
                key={item.key}
                className={`fappTutorColleges__checkRow ${checked ? 'fappTutorColleges__checkRow--done' : ''}`}
            >
                <span className='fappTutorColleges__checkBox'>{checked && <MdCheck size={13} />}</span>
                <span className='fappTutorColleges__checkText'>
                    <span className='fappTutorColleges__checkLabel'>{item.label}</span>
                    {item.detail && <span className='fappTutorColleges__checkDetail'>{item.detail}</span>}
                </span>
            </div>
        );
    };

    return (
        <aside className='fappTutorColleges__summary'>
            <div className='fappTutorColleges__summaryGroup'>
                <h4>Checklist</h4>
                {dates.map(date => (
                    <div key={date} className='fappTutorColleges__dueGroup'>
                        <div className='fappTutorColleges__dueDate'>Due {formatDeadline(date)}</div>
                        {groups.get(date)!.map(row)}
                    </div>
                ))}
                {groups.has(null) && (
                    <div className='fappTutorColleges__dueGroup'>
                        <div className='fappTutorColleges__dueDate fappTutorColleges__dueDate--none'>No deadline set</div>
                        {groups.get(null)!.map(row)}
                    </div>
                )}
            </div>
        </aside>
    );
}

// Read-only view of the student's college list with each college's Common App
// requirements — the tutor-side mirror of the student dashboard section.
function FillAppColleges() {
    const { studentOrganizationId } = useParams<{ studentOrganizationId: string }>();

    const [payload, setPayload] = useState<ReviewPayload | null>(null);
    const [error, setError] = useState<string | null>(null);

    const cycleYear = payload?.fillApp?.cycleYear || DEFAULT_CYCLE_YEAR;
    const { grid, error: gridError } = useFillAppColleges(cycleYear);

    useEffect(() => {
        if (!studentOrganizationId) return;
        let cancelled = false;
        axios.get(`fillapp/tutor/review/${studentOrganizationId}/`)
            .then(res => { if (!cancelled) setPayload(res.data); })
            .catch(err => {
                if (!cancelled) setError(err?.response?.data?.message || 'Failed to load colleges.');
            });
        return () => { cancelled = true; };
    }, [studentOrganizationId]);

    const byId = useMemo(() => {
        const m = new Map<string, College>();
        (grid?.colleges || []).forEach(c => m.set(c.id, c));
        return m;
    }, [grid]);

    const studentName = payload?.studentName || '';
    const choices = payload?.fillApp?.colleges || [];
    const selected = useMemo(
        () => choices.map(ch => byId.get(ch.id)).filter(Boolean) as College[],
        [choices, byId],
    );
    const plans = useMemo(
        () => Object.fromEntries(choices.filter(ch => ch.plan).map(ch => [ch.id, ch.plan])),
        [choices],
    );

    return (
        <div className='fappTutorColleges'>
            <Helmet><title>{studentName ? `${studentName} - Colleges` : 'Colleges'}</title></Helmet>
            <Header
                title='Colleges'
                prevRoutes={[
                    { name: 'College Apps', path: '/college-apps' },
                    ...(studentName ? [{ name: studentName, path: `/college-apps/review/${studentOrganizationId}` }] : []),
                ]}
            />
            <FillAppTabs studentOrganizationId={studentOrganizationId!} active='colleges' />

            {(error || gridError) && <div className='fappTutorColleges__error'>{error || gridError}</div>}
            {!payload && !error && <Loader />}

            {payload && !payload.fillApp && (
                <div className='fappTutorColleges__empty'>
                    {studentName} doesn't have College Apps enabled yet.
                </div>
            )}

            {payload && payload.fillApp && grid && (
                choices.length === 0 ? (
                    <div className='fappTutorColleges__empty'>
                        {studentName} hasn't added any colleges yet. Their picks from the
                        College Apps dashboard will show up here.
                    </div>
                ) : (
                    <div className='fappTutorColleges__layout'>
                        <div className='fappTutorColleges__main'>
                            <div className='fappTutorColleges__count'>
                                {choices.length} college{choices.length > 1 ? 's' : ''} on {studentName}'s list
                            </div>
                            <div className='fappTutorColleges__list'>
                                {selected.map(c => {
                                    const deadlines = deadlineSummary(c.deadlines);
                                    const recs = recsSummary(c.recs);
                                    const chosenPlan = plans[c.id] || (c.deadlines.rd ? 'rd' : null);
                                    return (
                                        <div key={c.id} className='fappTutorColleges__card'>
                                            <h3 className='fappTutorColleges__cardName'>{c.name}</h3>
                                            <div className='fappTutorColleges__deadlines'>
                                                {deadlines.length > 0
                                                    ? deadlines.map(d => (
                                                        <span
                                                            key={d.key}
                                                            className={`fappTutorColleges__deadline ${chosenPlan === d.key ? (d.key === 'rd' ? 'fappTutorColleges__deadline--onRd' : 'fappTutorColleges__deadline--on') : ''}`}
                                                            title={chosenPlan === d.key ? `Applying ${d.label}` : undefined}
                                                        >
                                                            <b>{d.label}</b> {d.value}
                                                        </span>
                                                    ))
                                                    : (
                                            <span className='fappTutorColleges__deadline'>
                                                See website for deadlines
                                            </span>
                                        )}
                                            </div>
                                            <div className='fappTutorColleges__reqs'>
                                                <span className='fappTutorColleges__req'>{feeLabel(c.fees)}{c.fees.us !== c.fees.intl && c.fees.intl != null ? ` ($${c.fees.intl} intl)` : ''}</span>
                                                {c.personalEssay && <span className='fappTutorColleges__req'>Personal essay</span>}
                                                {c.writingSupplement && <span className='fappTutorColleges__req'>Writing supplement</span>}
                                                {c.coursesGrades && <span className='fappTutorColleges__req'>Courses &amp; grades</span>}
                                                {c.portfolio && <span className='fappTutorColleges__req'>Portfolio ({c.portfolio === 'SR' ? 'SlideRoom' : 'college site'})</span>}
                                                {c.testPolicy && <span className='fappTutorColleges__req'>{TEST_POLICY_LABELS[c.testPolicy] || c.testPolicy}</span>}
                                                {recs && <span className='fappTutorColleges__req'>{recs}</span>}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            <p className='fappTutorColleges__source'>
                                Requirements from Common App's official first-year grid
                                {grid.updatedAt ? `, updated ${grid.updatedAt}` : ''}. Always
                                confirm details on the college's website.
                            </p>
                        </div>

                        {selected.length > 0 && (
                            <CombinedPanel colleges={selected} plans={plans} checklist={payload.fillApp.collegeChecklist || {}} />
                        )}
                    </div>
                )
            )}
        </div>
    );
}

export default FillAppColleges;
